import {
  Component,
  computed,
  inject,
  signal,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { DialogData } from '../../../../shared/models/dialog-data.model';
import { MatButtonModule } from '@angular/material/button';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  map,
  shareReplay,
  withLatestFrom,
  catchError,
  of,
} from 'rxjs';
import { WebsitesService } from '../../../../core/services/websites.service';
import { Server } from '../../../../shared/models/server.models';
import { AsyncPipe } from '@angular/common';
import { MailboxService } from '../../../../core/services/mailbox.service';

@Component({
  selector: 'app-add-mail-service-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    AsyncPipe,
  ],
  templateUrl: './add-mail-service-dialog.component.html',
  styleUrl: './add-mail-service-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddMailServiceDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<AddMailServiceDialogComponent>);

  readonly data = inject<
    DialogData & {
      servers: Server[];
    }
  >(MAT_DIALOG_DATA);

  private fb = inject(FormBuilder);

  private websitesService = inject(WebsitesService);

  private mailboxService = inject(MailboxService);

  form = this.fb.nonNullable.group({
    systemUserId: [0, [Validators.required]],
    websiteId: [0, [Validators.required]],
    server: ['', [Validators.required]],
  });

  get servers(): { label: string; value: string }[] {
    return this.data.servers.map((s) => ({ label: s.hostname, value: s._id }));
  }

  serverSearchText = signal<string>('');

  filteredServers = computed(() =>
    this.servers.filter((s) =>
      this.serverSearchText() ? s.label.includes(this.serverSearchText()) : true,
    ),
  );

  websiteSearchText = signal<string>('');

  websiteSearchText$ = toObservable(this.websiteSearchText);

  websites$ = combineLatest({
    domain: this.websiteSearchText$.pipe(debounceTime(500), distinctUntilChanged()),
    server: this.form.controls.server.valueChanges,
  }).pipe(
    switchMap(({ domain, server }) =>
      this.websitesService.searchWebsites({ domainSearch: domain, serverSearch: server }),
    ),
    map(({ Websites }) => Websites),
    shareReplay(1),
  );

  websiteOptions$ = this.websites$.pipe(
    map((websites) => websites.map((w) => ({ label: w.domain, value: String(w.id) }))),
  );

  ngOnInit(): void {
    this.form.controls.websiteId.valueChanges
      .pipe(
        withLatestFrom(this.websites$),
        switchMap(([id, websites]) => {
          const selectedWebsite = websites.find((w) => w.id === +id);
          return selectedWebsite
            ? combineLatest({
                website: of(selectedWebsite),
                mailbox: this.mailboxService.getWebsiteMailbox(selectedWebsite.id).pipe(
                  catchError(() => {
                    return of(null);
                  }),
                ),
              })
            : of({ website: null, mailbox: null });
        }),
      )
      .subscribe(({ website, mailbox }) => {
        if (website) {
          this.form.controls.systemUserId.setValue(website.details.unixUserId);
        }
        if (mailbox) {
          this.form.controls.websiteId.setErrors({ serviceExists: true });
        }
      });
  }

  submit(): void {
    if (this.form.invalid) return;

    const { systemUserId, websiteId, server } = this.form.getRawValue();

    this.dialogRef.close({ systemUserId, websiteId: +websiteId, server });
  }

  close(): void {
    this.dialogRef.close();
  }
}
